import React, { Component } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { change } from 'redux-form';

import Grid from '@material-ui/core/Grid';

import { useSafeState } from 'services/hooks';
import { inChildren } from 'services/utils';

import {
  FormSearchSelectField,
  FormTextField,
  SimpleForm
} from 'components/Form';
import { useCan } from 'components/Rbac';
import Screen from 'components/Screen';

import * as actions from './actions';
import { validateEditForm } from './validation';

const defaultFormName = 'edit_geo_streets';

class Container extends Component {
  componentDidMount() {
    this.handleGetOne();
  }

  handleGetOne = async () => {
    const {
      match: { params: { id } = {} } = {},
      geoActions,
      history = {}
    } = this.props;
    try {
      await geoActions.getOne(id);
    } catch (err) {
      history.goBack();
    }
  };

  handleUpdate = async (data) => {
    const { geoActions, record = {}, loading, history } = this.props;
    if (loading) return;
    try {
      await geoActions.updateOne(record.id, data);
      history.goBack();
    } catch (err) {
      console.error(err);
    }
  };

  render() {
    const { children, ...rest } = this.props;
    return inChildren(children, {
      ...rest,
      handleUpdate: this.handleUpdate
    });
  }
}

const mapStateToProps = (state, ownProps) => {
  const { resourceName = '' } = ownProps;
  const target = state[resourceName] || {};
  const { one = {} } = target;
  return {
    loading: one.loading,
    record: one.data
  };
};

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  geoActions: bindActionCreators(actions, dispatch)
});

const ContainerConnect = connect(
  mapStateToProps,
  mapDispatchToProps
)(Container);

export const EditForm = (props) => {
  const dispatch = useDispatch();

  const {
    handleUpdate,
    loading,
    formName = defaultFormName,
    footerBar = true,
    record = {}
  } = props;

  const itemReceived = record && record.id;
  const record_region_id = record && record.region_id;
  const record_district_id = record && record.district_id;
  const record_locality_id = record && record.locality_id;

  const [regionId, setRegionId] = useSafeState(record_region_id);
  const [districtId, setDistrictId] = useSafeState(record_district_id);
  const [localityId, setLocalityId] = useSafeState(record_locality_id);

  let title = 'Редактировать';
  if (itemReceived) {
    title = `Редактировать улицу: ${record.name}`;
  }

  const onChangeLocalityHandler = React.useCallback(
    (locality) => {
      setLocalityId(locality ? locality.id : null);
      dispatch(change(formName, 'locality_district', ''));
    },
    [dispatch, formName, setLocalityId]
  );

  const onChangeDistrictHandler = React.useCallback(
    (district) => {
      setDistrictId(district ? district.id : null);
      dispatch(change(formName, 'locality_id', null));
      onChangeLocalityHandler(null);
    },
    [dispatch, formName, onChangeLocalityHandler, setDistrictId]
  );

  const onChangeRegionHandler = React.useCallback(
    (region) => {
      setRegionId(region ? region.id : null);
      dispatch(change(formName, 'district_id', null));
      onChangeDistrictHandler(null);
    },
    [dispatch, formName, onChangeDistrictHandler, setRegionId]
  );

  React.useEffect(() => {
    setRegionId(record_region_id);
  }, [record_region_id, setRegionId]);

  React.useEffect(() => {
    setDistrictId(record_district_id);
  }, [record_district_id, setDistrictId]);

  React.useEffect(() => {
    setLocalityId(record_locality_id);
  }, [record_locality_id, setLocalityId]);

  const history = useHistory();
  const back = history.goBack;

  const is_writable = useCan('geo:write');

  if (!is_writable) {
    return null;
  }

  return (
    <Screen title={title}>
      <SimpleForm
        loading={loading}
        form={formName}
        onSubmit={handleUpdate}
        disabled={loading}
        onBack={back}
        direction="row"
        footerBar={footerBar}
        validate={validateEditForm}
        initialValues={{ ...record }}
        parse={(values) => {
          const { name, alt_name, old_name, name_lang1, name_lang2 } = values;
          const res = {
            name: name ? name.trim() : '',
            alt_name: alt_name ? alt_name.trim() : '',
            old_name: old_name ? old_name.trim() : '',
            name_lang1: name_lang1 ? name_lang1.trim() : '',
            name_lang2: name_lang2 ? name_lang2.trim() : '',
            locality_id: values.locality_id ? values.locality_id.id : null,
            weight: values.weight ? parseInt(values.weight) : 0,
            locality_district: values.locality_district
              ? values.locality_district.trim()
              : ''
          };
          return res;
        }}>
        <Grid item xs={12}>
          <FormTextField source="name" label="Название" disabled={loading} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormSearchSelectField
            source="region_id"
            label="Регион"
            disabled={loading}
            requestPath="/geo/regions"
            responseFormat={(v) => v.results}
            renderOption={(it) => it.name}
            searchBy="name"
            onChangeHandler={onChangeRegionHandler}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormSearchSelectField
            source="district_id"
            label="Район"
            disabled={loading || !regionId}
            requestPath="/geo/district"
            responseFormat={(v) => v.results}
            renderOption={(it) => it.name}
            searchBy="name"
            where={regionId ? { region_id: regionId } : null}
            onChangeHandler={onChangeDistrictHandler}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormSearchSelectField
            source="locality_id"
            label="Нас. пункт"
            disabled={loading || !regionId}
            requestPath="/geo/locality"
            responseFormat={(v) => v.results}
            renderOption={(it) => it.name}
            searchBy="name"
            where={{
              region_id: regionId || undefined,
              district_id: districtId || undefined
            }}
            onChangeHandler={onChangeLocalityHandler}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormTextField
            source="locality_district"
            label="Район нас. пункта"
            disabled={loading || !localityId}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormTextField
            source="alt_name"
            label="Альтернативное название"
            disabled={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormTextField
            source="old_name"
            label="Старое название"
            disabled={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormTextField
            source="name_lang1"
            label="Язык №1"
            disabled={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormTextField
            source="name_lang2"
            label="Язык №2:"
            disabled={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormTextField
            source="weight"
            label="Вес"
            disabled={loading}
            type="number"
            inputProps={{
              step: '1'
            }}
          />
        </Grid>
      </SimpleForm>
    </Screen>
  );
};

const LocalityEdit = (props) => (
  <ContainerConnect {...props}>
    <EditForm />
  </ContainerConnect>
);

export default LocalityEdit;
